import * as React from 'react'
import * as Lodash from 'lodash'
import { TextField as MaterialTextField, TextFieldProps } from '@design/pt-ui'

export type Props = {
  text?: TextFieldProps
  value?: number | null
  min?: number
  max?: number
  step?: number
  locked?: boolean
  disabled?: boolean
  debounce?: number
  onPendingChange?: (pending: boolean) => any
  onChange?: (value: number | null) => any
}

const toNumber = (raw: string): number | null => {
  const parsed = Number(raw)
  return raw.trim() === '' || !Number.isFinite(parsed) ? null : parsed
}

// Replaces what react-numeric-input did via snap + strict + precision={0}:
// round to the nearest step, force an integer, then clamp into [min, max].
const normalise = (
  value: number | null,
  { min, max, step = 1 }: Props,
): number | null =>
  value === null
    ? null
    : Lodash.clamp(
        Math.round(step > 0 ? Math.round(value / step) * step : value),
        min ?? -Infinity,
        max ?? Infinity,
      )

export default function DebouncedNumericInput(props: Props) {
  const timeout = React.useRef<any>(null)
  const updater = React.useRef<Function>(() => {})

  const [text, setText] = React.useState<string>(Lodash.toString(props.value))

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.locked) {
      return
    }

    const raw = event.target.value
    setText(raw)

    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    updater.current = () => {
      if (props.onPendingChange) {
        props.onPendingChange(false)
      }

      if (props.onChange) {
        props.onChange(normalise(toNumber(raw), props))
      }
    }

    if (props.onPendingChange) {
      props.onPendingChange(true)
    }

    timeout.current = setTimeout(() => {
      updater.current()
      updater.current = () => {}
    }, props.debounce || 500)
  }

  // Flush anything still pending and correct the displayed value once the user
  // is done typing, rather than fighting them mid-keystroke.
  const handleBlur = () => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    updater.current()
    updater.current = () => {}

    setText(Lodash.toString(normalise(toNumber(text), props)))
  }

  React.useEffect(() => {
    setText(Lodash.toString(props.value))

    if (timeout.current) {
      clearTimeout(timeout.current)
    }
  }, [props.value])

  return (
    <MaterialTextField
      {...(props.text || {})}
      type="number"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={props.disabled ?? props.text?.disabled}
      variant={props.text?.variant ?? 'outlined'}
      size={props.text?.size ?? 'small'}
      sx={{ width: '200px', ...props.text?.sx }}
      slotProps={{
        htmlInput: {
          min: props.min,
          max: props.max,
          step: props.step ?? 1,
        },
      }}
    />
  )
}
